/**
 * The sheet as a project, not as a picture.
 *
 * Saving the whole canvas to the Cloud writes a PNG — one flat picture, which is what you want
 * to send someone. Opening it again gave you back exactly that: one picture, where a heading was
 * no longer a heading and a figure could no longer be moved. So a whole-canvas Cloud save now
 * writes a second, small file beside it holding the sheet itself — every object, where it is,
 * how big it is, what it says — and opening that item restores the sheet with everything
 * separately editable again, exactly as it was left.
 *
 * The PNG stays the primary file: anything else that reads the Cloud library still sees a normal
 * picture, and a project file with no picture beside it is never made.
 */

export const PROJECT_SUFFIX = '.cstudio.json';
export const PROJECT_VERSION = 1;

/**
 * 'folder/name.png' -> 'folder/name.png.cstudio.json'
 *
 * The extension STAYS. It used to be stripped, which meant the sheet file was named after the
 * picture's base name alone — so a whole sheet saved as CStudio_Image.png and a single photo
 * saved as CStudio_Image.jpg both answered to CStudio_Image, and the one sheet file belonged to
 * both of them. Opening the single photo opened the other one's whole sheet. Two saves, the
 * same name, different formats: an ordinary thing to do, and it swapped your work for someone
 * else's. Keeping the extension makes the name unique per file, because the file name is.
 */
export const projectPathFor = (imagePath) => String(imagePath) + PROJECT_SUFFIX;

/** How those files were named before the above. Read-only: old saves still open. */
export const legacyProjectPathFor = (imagePath) =>
  String(imagePath).replace(/\.[^./]+$/, '') + PROJECT_SUFFIX;

/** The image path a project file belongs to — the whole path now, the base name for old ones. */
export const projectBaseOf = (path) =>
  String(path).endsWith(PROJECT_SUFFIX) ? String(path).slice(0, -PROJECT_SUFFIX.length) : null;

/**
 * A blob: URL lives and dies with the page that made it, so it cannot be written into a file
 * that has to be read back tomorrow. Data URLs are already self-contained and pass through.
 */
async function inlineUrl(url, cache) {
  if (typeof url !== 'string') return url;
  if (!url.startsWith('blob:')) return url;
  // Converting a photograph to base64 is not free, and the autosave rebuilds the sheet every
  // few seconds while somebody is working. The same blob URL always yields the same bytes, so
  // it is worth doing once: the cache is what keeps a running autosave off the main thread.
  if (cache && cache.has(url)) return cache.get(url);
  try {
    const blob = await fetch(url).then(r => r.blob());
    const out = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    if (cache) cache.set(url, out);
    return out;
  } catch {
    return url;   // unreadable: better a project with one broken picture than no project
  }
}

/**
 * Everything needed to put the sheet back. Images carry their own bytes — a photo's pixels are
 * the one thing that cannot be recreated from anything else, and the whole point is to reopen
 * the sheet at full quality rather than at the quality of a flattened copy.
 */
export async function buildProject(state, { urlCache = null, keepOriginals = false } = {}) {
  const images = await Promise.all((state.images || []).map(async (im) => ({
    ...im,
    url: await inlineUrl(im.url, urlCache),
    // Working state that belongs to the moment, not to the sheet. The untouched copy a selfie
    // effect can be undone from is one of these: it is a blob: URL that dies with the page, and
    // the picture itself carries the effect, so the sheet reopens looking right — what is lost
    // is only the ability to take that one effect back off again.
    //
    // keepOriginals is for the autosave, where that IS worth carrying: coming back to the app
    // after the phone put it to sleep should leave you exactly where you were, effect still
    // removable, not one step past it.
    _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined,
    _fxOrigUrl: keepOriginals && im._fxOrigUrl ? await inlineUrl(im._fxOrigUrl, urlCache) : undefined,
  })));
  return {
    app: 'CreativeStudio',
    v: PROJECT_VERSION,
    savedAt: new Date().toISOString(),
    printSize: state.printSize,
    canvasBackground: state.canvasBackground,
    canvasBgFx: state.canvasBgFx ?? null,
    images,
    shapes: state.shapes || [],
    paths: state.paths || [],
  };
}

/** True for something this app wrote and this version understands. */
export const isProject = (d) =>
  !!d && d.app === 'CreativeStudio' && Number(d.v) >= 1 && Array.isArray(d.images);

/** The project as a file ready to upload. */
export const projectFile = (project, name) =>
  new File([JSON.stringify(project)], name, { type: 'application/json' });
